import PropTypes from "prop-types";
import { useContext, useMemo, useState, useEffect, useCallback } from "react";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DataTableCustom from "components/Tables/DataTableCustom";

import {
    Modal,
    Card,
    Grid,
    Icon,
    TextField,
    Autocomplete,
    Select,
    MenuItem,
    CircularProgress,
    InputLabel,
    Menu,
    Tooltip,
    Fade,
} from "@mui/material";

import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { getUproColor } from "utils/colors";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: { xs: "95%", sm: "90%", md: "80%", xxl: "50%" },
    overflowY: "auto",
    p: 3,
};

const GET_USUARIOS = gql`
  query getUsuariosCalendario($search: String) {
    usuarios(
      search: $search
      pagination: { page: 1, limit: 50 }
      order: { field: "apellido", order: ASC }
    ) {
      id
      nombre
      apellido
      dni
    }
  }
`;

const GET_TAGS_TAREA = gql`
    query tagsTarea($search: String) {
        tagsTarea(search: $search) {
            id
            nombre
        }
    }
`;

const CREAR_TAG_TAREA = gql`
    mutation crearTagTarea($nombre: String!) {
        crearTagTarea(nombre: $nombre) {
            id
            nombre
        }
    }
`;

const CREAR_TAREA = gql`
  mutation crearTarea($input: CrearTareaInput!) {
    crearTarea(input: $input) {
      id
      titulo
      observacion
      fecha
      estado
      periodicidad
      fechaLimite
      tipoTarea
      turno
      idUser
      idTag
      responsable { id nombre apellido dni }
      tag { id nombre }
      items { id texto tipoItem idMateriaPrima unidadAbreviatura }
    }
  }
`;

const EDITAR_TAREA = gql`
  mutation editarTarea($input: EditarTareaInput!) {
    editarTarea(input: $input) {
      id
      titulo
      observacion
      fecha
      estado
      periodicidad
      fechaLimite
      tipoTarea
      turno
      idUser
      idTag
      responsable { id nombre apellido dni }
      tag { id nombre }
      items { id texto tipoItem idMateriaPrima unidadAbreviatura }
    }
  }
`;

const CREAR_TAREA_PLANTILLA = gql`
    mutation crearTareaPlantilla($input: CrearTareaPlantillaInput!) {
        crearTareaPlantilla(input: $input) {
            id
            titulo
        }
    }
`;

const GET_TAREA_PLANTILLAS = gql`
    query tareaPlantillas($search: String) {
        tareaPlantillas(search: $search) {
            id
            titulo
            observacion
            tipoTarea
            turno
            idTag
            tag { id nombre }
            items { id texto checked tipoItem idMateriaPrima unidadAbreviatura }
        }
    }
`;

const ELIMINAR_TAREA_PLANTILLA = gql`
    mutation eliminarTareaPlantilla($id: ID!) {
        eliminarTareaPlantilla(id: $id) {
            id
        }
    }
`;

const GET_MATERIAS_PRIMAS = gql`
    query getMateriasPrimas($filter: JSON, $idSucursal: ID) {
        materiasPrimas(pagination: { page: 1, limit: 50 }, filter: $filter) {
            id
            nombre
            categoria
            disabled
            tipoUnidad { abreviatura }
            stock(idSucursal: $idSucursal) { cantidad }
        }
    }
`;

function formatUsuarioLabel(u) {
    if (!u) return "";
    const dni = u?.dni ? ` - DNI: ${u.dni} ` : "";
    return `${u.apellido || ""}, ${u.nombre || ""}${dni} `.trim();
}

const mapMateriaPrimaItem = (mp, tipoItem) => ({
    idMateriaPrima: Number(mp.id),
    nombre: mp.nombre,
    stock: mp.stock?.cantidad ?? 0,
    unidadAbreviatura: mp.tipoUnidad?.abreviatura || "",
    disponible: !mp.disabled,
    tipoItem,
});

export default function ModalControlCafeteria({ open, onClose, selectedDate, initialTask, onSaved }) {
    const { handleSnackbar } = useContext(MessageManager);
    const { confirmar } = useContext(ConfirmarContext);
    const [consumibles, setConsumibles] = useState([]);
    const [materiales, setMateriales] = useState([]);


    const { data: materiaPrimaData, refetch: refetchMateriaPrima, loading: loadingMateriaPrima } = useQuery(
        gql`
      query materiasPrimas($search: String) {
        materiasPrimas(
          search: $search
          pagination: { page: 1, limit: 20 }
          filter: { tipo: ["Consumible", "Plato_Ingrediente"] }
        ) {
          id
          nombre
          precio
          porcentajeUtilizable
          tipo
          categoria
          disabled
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
        }
      }
    `
    );

    const isEdit = Boolean(initialTask?.id);

    const [titulo, setTitulo] = useState("");
    const [observacion, setObservacion] = useState("");
    const [turno, setTurno] = useState("MANANA");
    const [searchUsuario, setSearchUsuario] = useState("");
    const [responsable, setResponsable] = useState(null);
    const [searchTag, setSearchTag] = useState("");
    const [tag, setTag] = useState(null);

    const [productos, setProductos] = useState([]);
    const [descartables, setDescartables] = useState([]);

    const [productoSeleccionado, setProductoSeleccionado] = useState(null);
    const [descartableSeleccionado, setDescartableSeleccionado] = useState(null);

    const [timeoutProducto, setTimeoutProducto] = useState(null);
    const [timeoutDescartable, setTimeoutDescartable] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [openModalAddPlantiilla, setOpenModalAddPlantiilla] = useState(false);
    const [openMenuPlantilla, setOpenMenuPlantilla] = useState(false);
    const [administrarPlantillas, setAdministrarPlantillas] = useState(false);
    const [data, setData] = useState();

    const { data: usuariosData, loading: loadingUsuarios } = useQuery(GET_USUARIOS, {
        variables: { search: searchUsuario || null },
        fetchPolicy: "network-only",
        skip: !open,
    });

    const { data: tagsData, loading: loadingTags } = useQuery(GET_TAGS_TAREA, {
        variables: { search: searchTag || null },
        fetchPolicy: "network-only",
        skip: !open,
    });

    const usuariosOptions = useMemo(() => usuariosData?.usuarios || [], [usuariosData]);
    const tagsOptions = useMemo(() => tagsData?.tagsTarea || [], [tagsData]);

    const [crearTarea, { loading: loadingCreate }] = useMutation(CREAR_TAREA);
    const [editarTarea, { loading: loadingEdit }] = useMutation(EDITAR_TAREA);
    const [crearTagTarea, { loading: loadingCreateTag }] = useMutation(CREAR_TAG_TAREA);
    const [crearTareaPlantilla, { loading: loadingPlantilla }] = useMutation(CREAR_TAREA_PLANTILLA);
    const [eliminarTareaPlantilla, { loading: loadingDeletePlantilla }] = useMutation(ELIMINAR_TAREA_PLANTILLA, {
        refetchQueries: [{ query: GET_TAREA_PLANTILLAS }],
        awaitRefetchQueries: true,
    });

    const { data: plantillasData, loading: loadingPlantillas } = useQuery(GET_TAREA_PLANTILLAS, {
        fetchPolicy: "network-only",
        skip: !open,
    });

    const [buscarProductos, { data: productosData, loading: loadingProductos }] = useLazyQuery(GET_MATERIAS_PRIMAS, {
        fetchPolicy: "network-only",
    });

    const [buscarDescartables, { data: descartablesData, loading: loadingDescartables }] = useLazyQuery(GET_MATERIAS_PRIMAS, {
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (!open) return;

        if (isEdit) {
            setTitulo(initialTask?.titulo || "");
            setObservacion(initialTask?.observacion || "");
            setTurno(initialTask?.turno || "MANANA");
            setResponsable(initialTask?.responsable || null);
            setTag(initialTask?.tag || null);

            const items = initialTask?.items || [];
            const productosList = items
                .filter((it) => it.tipoItem !== "DESCARTABLE")
                .map((it) => ({
                    idMateriaPrima: it.idMateriaPrima,
                    nombre: it.texto,
                    stock: null,
                    unidadAbreviatura: it.unidadAbreviatura || "",
                    disponible: true,
                    tipoItem: "PRODUCTO",
                }));
            const descartablesList = items
                .filter((it) => it.tipoItem === "DESCARTABLE")
                .map((it) => ({
                    idMateriaPrima: it.idMateriaPrima,
                    nombre: it.texto,
                    stock: null,
                    unidadAbreviatura: it.unidadAbreviatura || "",
                    disponible: true,
                    tipoItem: "DESCARTABLE",
                }));

            setProductos(productosList);
            setDescartables(descartablesList);
        } else {
            setTitulo("");
            setObservacion("");
            setTurno("MANANA");
            setResponsable(null);
            setTag(null);
            setProductos([]);
            setDescartables([]);
        }
    }, [open, isEdit, initialTask]);

    useEffect(() => {
        if (materiaPrimaData) {
            let consumibles = materiaPrimaData.materiasPrimas.filter(
                (item) =>
                    ([
                        "Alimentos",
                        "Verduras y Frutas",
                        "Carnes",
                        "Lácteos",
                        "Granos y Cereales",
                        "Aceites y Grasas",
                        "Bebidas",
                        "Condimentos",
                        "Empaques y Envases",
                        "Cafetería",
                        "Pastelería",
                        "Semillas",
                        "Legumbres",
                        "Congelados",
                        "Panadería",
                        "Fiambre",
                        "Embutidos",
                    ].includes(item.categoria) ||
                        item.tipo === "Plato_Ingrediente") &&
                    item?.disabled === false
            );
            let materiales = materiaPrimaData.materiasPrimas.filter(
                (item) => item.categoria === "Descartables"
            );
            setConsumibles(consumibles);
            setMateriales(materiales);
        }
    }, [materiaPrimaData]);

    const handleSearchChange = useCallback((e) => {
        refetchMateriaPrima({
            search: e?.target?.value || "",
        });
    }, [refetchMateriaPrima]);

    const canSubmit = useMemo(() => {
        if (!titulo.trim()) return false;
        if (!selectedDate || !selectedDate.isValid?.()) return false;
        if (!turno) return false;
        const idUser = Number(responsable?.id);
        const idTag = Number(tag?.id);
        if (!Number.isFinite(idUser) || idUser <= 0) return false;
        if (!Number.isFinite(idTag) || idTag <= 0) return false;
        if (productos.length === 0 && descartables.length === 0) return false;
        if (!isEdit && selectedDate && selectedDate.isBefore(dayjs(), "day")) return false;
        return true;
    }, [titulo, selectedDate, turno, responsable, tag, productos, descartables, isEdit]);

    const productOptions = useMemo(() => productosData?.materiasPrimas || [], [productosData]);
    const descartableOptions = useMemo(() => descartablesData?.materiasPrimas || [], [descartablesData]);

    const handleSearchProducto = (event) => {
        const searchValue = event.target.value || "";
        if (timeoutProducto) clearTimeout(timeoutProducto);
        const newTimeout = setTimeout(() => {
            if (!searchValue.trim()) return;
            buscarProductos({
                variables: {
                    idSucursal: 1,
                    filter: {
                        _and: [
                            { categoria: { _ne: "Descartables" } },
                            {
                                _or: [
                                    { id: { _like: `%${searchValue}%` } },
                                    { nombre: { _like: `%${searchValue}%` } },
                                    { categoria: { _like: `%${searchValue}%` } },
                                ],
                            },
                        ],
                    },
                },
            });
        }, 500);
        setTimeoutProducto(newTimeout);
    };

    const handleSearchDescartable = (event) => {
        const searchValue = event.target.value || "";
        if (timeoutDescartable) clearTimeout(timeoutDescartable);
        const newTimeout = setTimeout(() => {
            if (!searchValue.trim()) return;
            buscarDescartables({
                variables: {
                    idSucursal: 1,
                    filter: {
                        _and: [
                            { categoria: "Descartables" },
                            {
                                _or: [
                                    { id: { _like: `%${searchValue}%` } },
                                    { nombre: { _like: `%${searchValue}%` } },
                                    { categoria: { _like: `%${searchValue}%` } },
                                ],
                            },
                        ],
                    },
                },
            });
        }, 500);
        setTimeoutDescartable(newTimeout);
    };

    const addItemToList = (listSetter, list, mp, tipoItem) => {
        if (!mp) return;
        const mapped = mapMateriaPrimaItem(mp, tipoItem);
        if (list.some((it) => it.idMateriaPrima === mapped.idMateriaPrima)) return;
        listSetter([...list, mapped]);
    };

    const handleSubmit = async () => {
        if (!canSubmit) {
            handleSnackbar("Completa todos los campos requeridos", "error");
            return;
        }

        try {
            const idUser = Number(responsable?.id);
            const idTag = Number(tag?.id);
            const itemsControl = [...productos, ...descartables].map((it) => ({
                idMateriaPrima: it.idMateriaPrima,
                nombre: it.nombre,
                tipoItem: it.tipoItem,
                unidadAbreviatura: it.unidadAbreviatura || null,
            }));

            if (isEdit) {
                await editarTarea({
                    variables: {
                        input: {
                            id: initialTask.id,
                            titulo: titulo.trim(),
                            observacion: observacion?.trim() || null,
                            turno,
                            idUser,
                            idTag,
                            itemsControl,
                        },
                    },
                });
                handleSnackbar("Tarea modificada", "success");
            } else {
                await crearTarea({
                    variables: {
                        input: {
                            titulo: titulo.trim(),
                            observacion: observacion?.trim() || null,
                            fecha: selectedDate.format("YYYY-MM-DD"),
                            periodicidad: "UNICA",
                            fechaLimite: null,
                            tipoTarea: "CONTROL_CAFETERIA",
                            turno,
                            idUser,
                            idTag,
                            itemsControl,
                            items: [],
                        },
                    },
                });
                handleSnackbar("Tarea creada", "success");
            }

            onSaved?.();
            onClose?.();
        } catch (e) {
            handleSnackbar(e?.message || "Error al guardar", "error");
        }
    };

    const handleSavePlantilla = async () => {
        if (!data?.nombre) return;

        try {
            const itemsControl = [...productos, ...descartables].map((it) => ({
                idMateriaPrima: it.idMateriaPrima,
                nombre: it.nombre,
                tipoItem: it.tipoItem,
                unidadAbreviatura: it.unidadAbreviatura || null,
            }));

            await crearTareaPlantilla({
                variables: {
                    input: {
                        titulo: data.nombre.trim(),
                        observacion: observacion?.trim() || null,
                        fecha: selectedDate.format("YYYY-MM-DD"),
                        periodicidad: "UNICA",
                        fechaLimite: null,
                        tipoTarea: "CONTROL_CAFETERIA",
                        turno,
                        idTag: Number(tag.id),
                        items: [],
                        itemsControl,
                    },
                },
            });
            handleSnackbar("Plantilla guardada con éxito", "success");
            setOpenModalAddPlantiilla(false);
            setData(null);
        } catch (e) {
            handleSnackbar(e?.message || "Error al guardar plantilla", "error");
        }
    };

    const loading = loadingCreate || loadingEdit || loadingCreateTag;
    const fechaLabel = selectedDate ? selectedDate.format("DD/MM/YYYY") : "-";

    const handleOpenPlantillaMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setOpenMenuPlantilla(true);
    };

    const handleClosePlantillaMenu = () => {
        setAnchorEl(null);
        setOpenMenuPlantilla(false);
    };

    const plantillas = useMemo(() => {
        const all = plantillasData?.tareaPlantillas || [];
        return all.filter((p) => p?.tipoTarea === "CONTROL_CAFETERIA");
    }, [plantillasData]);

    const handleApplyPlantilla = (plantilla) => {
        if (plantilla.titulo) setTitulo(plantilla.titulo);
        if (plantilla.observacion) setObservacion(plantilla.observacion);
        if (plantilla.tag) setTag(plantilla.tag);
        if (plantilla.turno) setTurno(plantilla.turno);

        const items = plantilla.items || [];
        const productosList = items
            .filter((it) => it.tipoItem !== "DESCARTABLE")
            .map((it) => ({
                idMateriaPrima: it.idMateriaPrima,
                nombre: it.texto,
                stock: null,
                unidadAbreviatura: it.unidadAbreviatura || "",
                disponible: true,
                tipoItem: "PRODUCTO",
            }));
        const descartablesList = items
            .filter((it) => it.tipoItem === "DESCARTABLE")
            .map((it) => ({
                idMateriaPrima: it.idMateriaPrima,
                nombre: it.texto,
                stock: null,
                unidadAbreviatura: it.unidadAbreviatura || "",
                disponible: true,
                tipoItem: "DESCARTABLE",
            }));

        setProductos(productosList);
        setDescartables(descartablesList);

        handleClosePlantillaMenu();
        handleSnackbar("Plantilla aplicada", "success");
    };

    const handleDeletePlantilla = async (plantilla) => {
        if (!plantilla?.id) return;
        confirmar({
            title: "Eliminar plantilla",
            message: `¿Está seguro que desea eliminar la plantilla "${plantilla.titulo}"?`,
            func: async () => {
                try {
                    await eliminarTareaPlantilla({ variables: { id: plantilla.id } });
                    handleSnackbar("Plantilla eliminada", "success");
                } catch (e) {
                    handleSnackbar(e.message || "Error al eliminar plantilla", "error");
                }
            },
        });
    };

    const renderMenuPlantillas = (
        <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            open={Boolean(openMenuPlantilla)}
            onClose={handleClosePlantillaMenu}
        >
            {plantillas?.length > 0 ? (
                plantillas?.map((plantilla) => (
                    <Tooltip
                        key={plantilla.id}
                        title={`${administrarPlantillas ? "Eliminar" : ""} ${plantilla?.titulo} (${plantilla?.items?.length} items)`}
                        placement="left"
                    >
                        <MenuItem
                            onClick={() => {
                                if (administrarPlantillas) handleDeletePlantilla(plantilla);
                                else if (productos.length || descartables.length) {
                                    confirmar({
                                        title: "Sobreescribir items",
                                        message: "Esta acción sobreescribirá los items actuales, ¿desea continuar?",
                                        icon: "warning",
                                        func: () => {
                                            handleApplyPlantilla(plantilla);
                                        },
                                    });
                                } else {
                                    handleApplyPlantilla(plantilla);
                                }
                            }}
                            disabled={loadingPlantillas || loadingDeletePlantilla}
                        >
                            <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                    width: "100%",
                                }}
                            >
                                <SoftBox display="flex" mr={2}>
                                    <SoftBox mr={1}>
                                        <SoftTypography variant="p" fontWeight="bold">
                                            {plantilla.titulo.length > 20
                                                ? `${plantilla.titulo.substring(0, 20)}...`
                                                : plantilla.titulo}
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox>
                                        <SoftTypography variant="caption" color="uproGreen">
                                            {`${plantilla?.items?.length} items`}
                                        </SoftTypography>
                                    </SoftBox>
                                </SoftBox>
                                <SoftBox>
                                    <Icon
                                        fontSize="small"
                                        sx={{
                                            verticalAlign: "middle",
                                            color: administrarPlantillas
                                                ? getUproColor("uproPink")
                                                : getUproColor("uproGreen"),
                                        }}
                                    >
                                        {administrarPlantillas ? "delete" : "playlist_add"}
                                    </Icon>
                                </SoftBox>
                            </SoftBox>
                        </MenuItem>
                    </Tooltip>
                ))
            ) : (
                <SoftTypography variant="caption" color="text" p={2}>
                    Sin datos
                </SoftTypography>
            )}
            {plantillas?.length > 0 && (
                <MenuItem
                    onClick={() => setAdministrarPlantillas(!administrarPlantillas)}
                    disabled={loadingPlantillas || loadingDeletePlantilla}
                >
                    <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            width: "100%",
                        }}
                    >
                        <SoftBox display="flex" mr={2}>
                            <SoftBox mr={1}>
                                <SoftTypography variant="p" fontWeight="bold">
                                    {administrarPlantillas ? "Cancelar" : "Administrar"}
                                </SoftTypography>
                            </SoftBox>
                        </SoftBox>
                        <SoftBox>
                            <Icon
                                fontSize="small"
                                sx={{
                                    verticalAlign: "middle",
                                    color: getUproColor("uproYellow"),
                                }}
                            >
                                {administrarPlantillas ? "arrow_back" : "settings"}
                            </Icon>
                        </SoftBox>
                    </SoftBox>
                </MenuItem>
            )}
        </Menu>
    );

    const renderH = (column, isCurrentSort, direction, sortable) => (
        <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ width: "100%", overflow: "hidden", minWidth: 0 }}
        >
            <SoftBox display="flex" alignItems="center" gap={1} sx={{ width: "100%" }} >
                <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="dark"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        minWidth: 0,
                    }}
                    title={column.headerName}
                >
                    {column.headerName}
                </SoftTypography>
            </SoftBox>

            {sortable && (
                isCurrentSort && direction ? (
                    <Icon fontSize="small" sx={{ color: "primary.main", opacity: 0.8 }}>
                        {direction === "asc" ? "arrow_drop_up" : "arrow_drop_down"}
                    </Icon>
                ) : (
                    <Icon
                        fontSize="small"
                        sx={{
                            color: "text.secondary",
                            opacity: 0.4,
                            "&:hover": { opacity: 0.8 },
                        }}
                    >
                        import_export
                    </Icon>
                )
            )}
        </SoftBox>
    );


    const renderItemsTable = (title, items, onRemove) => {
        const dataTableData = (items || []).map((item) => ({
            ...item,
            stockLabel: `${item?.stock ?? "-"} ${item?.unidadAbreviatura || ""}`.trim(),
            disponibleLabel: item?.disponible === false ? "No disponible" : "Disponible",
        }));

        const columns = [
            {
                headerName: "Nombre",
                field: "nombre",
                width: "90%",
                sticky: "left",
                renderHeader: (column, isCurrentSort, direction) => renderH(column, isCurrentSort, direction, column.sortable),
            },
            // {
            //     headerName: "Stock",
            //     field: "stockLabel",
            //     align: "center",
            //     sortable: false,
            //     renderHeader: (column, isCurrentSort, direction) => renderH(column, isCurrentSort, direction, column.sortable),
            // },
            {
                headerName: "Quitar",
                field: "",
                align: "center",
                sortable: false,
                renderHeader: (column, isCurrentSort, direction) => renderH(column, isCurrentSort, direction, column.sortable),
                renderCell: (row) => (
                    <Tooltip title="Quitar de la lista" placement="top">
                        <Icon
                            color="error"
                            sx={{ cursor: "pointer"}}
                            onClick={() => onRemove(row?.idMateriaPrima)}
                        >
                            delete
                        </Icon>
                    </Tooltip>
                ),
            },
        ];

        return (
            <SoftBox mt={2}>
                <SoftTypography variant="caption" fontWeight="bold">
                    {title}
                </SoftTypography>
                <DataTableCustom
                    headerStyles={{
                        backgroundColor: "#ffffff",             // fondo blanco
                        borderBottom: "1.5px solid #070606ff",
                        py: 1.1,                               // menos alto
                        "& .MuiTypography-root": {
                            fontWeight: 600,                      // negrita media
                            color: "#344767",                     // gris azulado
                            // textTransform: "none",                // sin uppercase
                            fontSize: "0.7rem",
                            letterSpacing: 0.2,
                        },
                    }}
                    columns={columns}
                    data={dataTableData}
                    loading={false}
                    showFooter={false}
                    emptyMessage="Sin items"
                    getRowId={(row) => row?.idMateriaPrima}
                    showActions={false}
                />
            </SoftBox>
        );
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Card sx={style}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <SoftTypography variant="h6" fontWeight="bold">
                        {isEdit ? "Editar control cafetería" : "Nuevo control cafetería"}
                    </SoftTypography>
                    <SoftButton variant="text" color="dark" iconOnly onClick={onClose}>
                        <Icon>close</Icon>
                    </SoftButton>
                </SoftBox>

                {!isEdit && (
                    <SoftBox display="flex" justifyContent="end" alignItems="center" mr={1}>
                        <SoftBox mr={2}>
                            <Tooltip title="Plantillas" placement="top">
                                <SoftButton
                                    circular
                                    color="uproGreen"
                                    onClick={handleOpenPlantillaMenu}
                                    disabled={loadingPlantillas || loadingDeletePlantilla}
                                >
                                    {(loadingPlantillas || loadingDeletePlantilla) && (
                                        <CircularProgress size={15} color="inherit" />
                                    )}
                                    {!loadingPlantillas && !loadingDeletePlantilla && <Icon>sort</Icon>}
                                    &nbsp;Plantillas
                                </SoftButton>
                            </Tooltip>
                        </SoftBox>
                        {renderMenuPlantillas}
                        <SoftBox>
                            <Tooltip title="Guardar como plantilla" placement="top">
                                <SoftButton
                                    circular
                                    iconOnly
                                    color="primary"
                                    disabled={!canSubmit || loading}
                                    onClick={() => setOpenModalAddPlantiilla(true)}
                                >
                                    <Icon>save</Icon>
                                </SoftButton>
                            </Tooltip>
                        </SoftBox>
                    </SoftBox>
                )}

                <SoftBox mb={2}>
                    <SoftTypography variant="h6" color="secondary" fontWeight="bold">
                        Fecha: {fechaLabel}
                    </SoftTypography>
                </SoftBox>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={9}>
                        <InputLabel htmlFor="titulo">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Título de la tarea
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                    &nbsp;*
                                </SoftTypography>
                            </SoftTypography>
                        </InputLabel>
                        <SoftInput
                            fullWidth
                            label="Título de la tarea"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <InputLabel htmlFor="turno">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Turno
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                    &nbsp;*
                                </SoftTypography>
                            </SoftTypography>
                        </InputLabel>
                        <Select
                            fullWidth
                            size="small"
                            value={turno}
                            sx={{
                                "& .MuiSelect-select": {
                                    paddingTop: "0px",
                                    paddingBottom: "0px",
                                    fontSize: "0.75rem"
                                },
                            }}
                            onChange={(e) => setTurno(e.target.value)}
                        >
                            <MenuItem value="MANANA">Mañana</MenuItem>
                            <MenuItem value="TARDE">Tarde</MenuItem>
                        </Select>
                    </Grid>

                    <Grid item xs={12}>
                        <InputLabel htmlFor="observacion">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Observación
                            </SoftTypography>
                        </InputLabel>
                        <SoftInput
                            fullWidth
                            value={observacion}
                            onChange={(e) => setObservacion(e.target.value)}
                            size="small"
                            multiline
                            rows={3}
                        />
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <InputLabel htmlFor="responsable">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Responsable
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                    &nbsp;*
                                </SoftTypography>
                            </SoftTypography>
                        </InputLabel>
                        <Autocomplete
                            options={usuariosOptions}
                            value={responsable}
                            loading={loadingUsuarios}
                            onChange={(e, newValue) => setResponsable(newValue)}
                            getOptionLabel={formatUsuarioLabel}
                            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                            onInputChange={(e, value) => setSearchUsuario(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingUsuarios ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <InputLabel htmlFor="tag">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Tag
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                    &nbsp;*
                                </SoftTypography>
                            </SoftTypography>
                        </InputLabel>
                        <Autocomplete
                            options={tagsOptions}
                            value={tag}
                            loading={loadingTags}
                            onChange={async (e, newValue) => {
                                if (newValue && typeof newValue === "string") {
                                    try {
                                        const res = await crearTagTarea({ variables: { nombre: newValue } });
                                        setTag(res?.data?.crearTagTarea || null);
                                    } catch (err) {
                                        handleSnackbar(err?.message || "Error al crear tag", "error");
                                    }
                                    return;
                                }

                                if (newValue && newValue.inputValue) {
                                    try {
                                        const res = await crearTagTarea({ variables: { nombre: newValue.inputValue } });
                                        setTag(res?.data?.crearTagTarea || null);
                                    } catch (err) {
                                        handleSnackbar(err?.message || "Error al crear tag", "error");
                                    }
                                    return;
                                }

                                setTag(newValue);
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === "string") return option;
                                if (option?.inputValue) return option.inputValue;
                                return option?.nombre || "";
                            }}
                            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                            onInputChange={(e, value) => setSearchTag(value)}
                            freeSolo
                            filterOptions={(options, params) => {
                                const filtered = options.filter((opt) =>
                                    (opt?.nombre || "").toLowerCase().includes((params.inputValue || "").toLowerCase())
                                );

                                const inputValue = (params.inputValue || "").trim();
                                const exists = options.some((opt) => (opt?.nombre || "").toLowerCase() === inputValue.toLowerCase());
                                if (inputValue !== "" && !exists) {
                                    filtered.push({ inputValue, nombre: `Crear: ${inputValue} ` });
                                }

                                return filtered;
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingTags ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor="ingredientes">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Producto / materia prima
                            </SoftTypography>
                        </InputLabel>
                        <Autocomplete
                            value={productoSeleccionado}
                            options={consumibles || []}
                            noOptionsText="No hay resultados"
                            onInputChange={handleSearchChange}
                            loading={loading}
                            loadingText="Cargando..."
                            onChange={(e, newValue) => setProductoSeleccionado(newValue)}
                            getOptionLabel={(opt) => {
                                if (!opt) return "";
                                const stock = opt?.stock?.cantidad ?? 0;
                                const unidad = opt?.tipoUnidad?.abreviatura || "";
                                const disponible = opt?.disabled ? "No disponible" : "Disponible";
                                return `${opt.nombre} - stock: ${stock} ${unidad} - ${disponible}`;
                            }}
                            renderInput={(params) => (
                                <TextField {...params} size="small" placeholder="Buscar materia prima" />
                            )}
                        />
                        <SoftButton
                            variant="outlined"
                            color="info"
                            size="small"
                            sx={{ mt: 1 }}
                            disabled={!productoSeleccionado}
                            onClick={() => {
                                addItemToList(setProductos, productos, productoSeleccionado, "PRODUCTO");
                                setProductoSeleccionado(null);
                            }}
                        >
                            <Icon sx={{ fontSize: 18 }}>add</Icon>
                            &nbsp;Agregar producto
                        </SoftButton>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <InputLabel htmlFor="descartable">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Descartable
                            </SoftTypography>
                        </InputLabel>
                        <Autocomplete
                            value={descartableSeleccionado}
                            options={materiales || []}
                            loading={loadingDescartables}
                            onChange={(e, newValue) => setDescartableSeleccionado(newValue)}
                            getOptionLabel={(opt) => {
                                if (!opt) return "";
                                const stock = opt?.stock?.cantidad ?? 0;
                                const unidad = opt?.tipoUnidad?.abreviatura || "";
                                const disponible = opt?.disabled ? "No disponible" : "Disponible";
                                return `${opt.nombre} - stock: ${stock} ${unidad} - ${disponible}`;
                            }}
                            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                            onInputChange={handleSearchChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Buscar descartable"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingDescartables ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <SoftButton
                            variant="outlined"
                            color="info"
                            size="small"
                            sx={{ mt: 1 }}
                            disabled={!descartableSeleccionado}
                            onClick={() => {
                                addItemToList(setDescartables, descartables, descartableSeleccionado, "DESCARTABLE");
                                setDescartableSeleccionado(null);
                            }}
                        >
                            <Icon sx={{ fontSize: 18 }}>add</Icon>
                            &nbsp;Agregar descartable
                        </SoftButton>
                    </Grid>
                </Grid>

                {renderItemsTable("Productos", productos, (id) => setProductos(productos.filter((p) => p.idMateriaPrima !== id)))}
                {renderItemsTable("Descartables", descartables, (id) => setDescartables(descartables.filter((p) => p.idMateriaPrima !== id)))}

                <SoftBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <SoftButton variant="outlined" color="dark" onClick={onClose}>
                        Cancelar
                    </SoftButton>
                    <SoftButton color="primary" onClick={handleSubmit} disabled={!canSubmit || loading}>
                        {loading ? "Guardando..." : "Guardar"}
                    </SoftButton>
                </SoftBox>

                <Modal
                    aria-labelledby="transition-modal-title"
                    aria-describedby="transition-modal-description"
                    open={openModalAddPlantiilla}
                    onClose={() => setOpenModalAddPlantiilla(false)}
                    closeAfterTransition
                >
                    <Fade in={openModalAddPlantiilla}>
                        <Card sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", maxHeight: "90%", width: { xs: "90%", sm: "40%", xxl: "30%" }, overflow: "auto" }}>
                            <SoftBox>
                                <SoftBox mb={1}>
                                    <SoftBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="start"
                                        p={3}
                                        pb={0}
                                    >
                                        <SoftBox>
                                            <SoftTypography variant="h6">Crear nueva plantilla</SoftTypography>
                                        </SoftBox>
                                        <SoftBox>
                                            <Tooltip title="Cerrar" placement="top">
                                                <Icon fontSize="medium" onClick={() => setOpenModalAddPlantiilla(false)} sx={{ cursor: "pointer" }}>
                                                    close
                                                </Icon>
                                            </Tooltip>
                                        </SoftBox>
                                    </SoftBox>
                                    <SoftBox p={3}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <InputLabel htmlFor="nombre">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        Nombre de la plantilla
                                                    </SoftTypography>
                                                </InputLabel>
                                                <SoftInput
                                                    label="nombre"
                                                    placeholder="Ingrese un nombre"
                                                    type="text"
                                                    name="nombre"
                                                    value={data?.nombre || ""}
                                                    onChange={(e) => {
                                                        setData({
                                                            ...data,
                                                            [e.target.name]: e.target.value,
                                                        });
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </SoftBox>
                                </SoftBox>
                                <SoftBox display="flex" justifyContent="end" alignItems="center" p={3} pt={0}>
                                    <SoftBox mr={2}>
                                        <SoftButton color="primary" circular onClick={() => setOpenModalAddPlantiilla(false)}>
                                            <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                                            &nbsp;Cancelar
                                        </SoftButton>
                                    </SoftBox>
                                    <SoftBox>
                                        <SoftButton
                                            color="uproGreen"
                                            circular
                                            disabled={!data?.nombre || loadingPlantilla}
                                            onClick={handleSavePlantilla}
                                        >
                                            {loadingPlantilla && <CircularProgress size={15} color="inherit" />}
                                            {!loadingPlantilla && <Icon sx={{ fontWeight: "light" }}>save</Icon>}
                                            &nbsp;Guardar
                                        </SoftButton>
                                    </SoftBox>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Fade>
                </Modal>
            </Card>
        </Modal>
    );
}

ModalControlCafeteria.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedDate: PropTypes.object,
    initialTask: PropTypes.object,
    onSaved: PropTypes.func,
};
